import React, { useState, useEffect, useCallback } from 'react';

/**
 * vLLM max_tokens 동적 계산 설정 모달
 * - 1페이지 base, 페이지당 추가, 절대 상한
 * - 4가지 프리셋 + 슬라이더 + 미리보기
 */
export default function VllmSettingsModal({ open, onClose }) {
  const [base, setBase] = useState(300);
  const [perPage, setPerPage] = useState(67);
  const [cap, setCap] = useState(1500);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      const r = await fetch('/api/settings/vllm-max-tokens');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setBase(d.base); setPerPage(d.perPage); setCap(d.cap);
    } catch {}
  }, []);

  useEffect(() => { if (open) loadConfig(); }, [open, loadConfig]);

  const calcMaxTokens = (pages) => Math.min(base + Math.max(0, pages - 1) * perPage, cap);
  const estimateLatency = (pages) => {
    const tok = calcMaxTokens(pages);
    return (3.5 + (pages - 1) * 1.5 + tok * 0.04).toFixed(1);
  };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      const r = await fetch('/api/settings/vllm-max-tokens', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ base, perPage, cap }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 3000);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const presets = [
    { name: '빠르게', base: 200, perPage: 50, cap: 1000 },
    { name: '기본', base: 300, perPage: 67, cap: 1500 },
    { name: '안전', base: 400, perPage: 100, cap: 2000 },
    { name: '인보이스', base: 500, perPage: 120, cap: 3000 },
  ];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-[600px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-base font-semibold">vLLM 추출 옵션 (max_tokens)</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">✕</button>
        </div>
        <div className="px-5 py-4 space-y-4 text-sm">
          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
            <code>max_tokens = base + (pages-1) × perPage</code> (cap 상한)
          </div>
          <div className="grid grid-cols-4 gap-2">
            {presets.map(p => (
              <button key={p.name}
                onClick={() => { setBase(p.base); setPerPage(p.perPage); setCap(p.cap); }}
                className="px-2 py-2 text-xs border rounded hover:bg-gray-50">
                {p.name}
              </button>
            ))}
          </div>
          {[
            ['1페이지 base', base, setBase, 100, 2000, 50],
            ['페이지당 perPage', perPage, setPerPage, 0, 300, 5],
            ['최대 cap', cap, setCap, Math.max(base, 500), 5000, 100],
          ].map(([lbl, v, set, mn, mx, st]) => (
            <div key={lbl}>
              <label className="flex justify-between text-xs mb-1">
                <span>{lbl}</span><span className="font-mono text-blue-600">{v}</span>
              </label>
              <input type="range" min={mn} max={mx} step={st} value={Math.max(v, mn)}
                onChange={(e) => set(parseInt(e.target.value))} className="w-full" />
            </div>
          ))}
          <div className="border rounded p-3 bg-gray-50">
            <div className="text-xs font-semibold mb-1">📊 미리보기</div>
            <table className="w-full text-xs">
              <thead><tr><th>페이지</th><th className="text-right">tokens</th><th className="text-right">latency</th></tr></thead>
              <tbody className="font-mono">
                {[1,3,5,10].map(p => (
                  <tr key={p} className="border-t">
                    <td>{p}p</td>
                    <td className="text-right text-blue-700">{calcMaxTokens(p)}</td>
                    <td className="text-right text-emerald-700">{estimateLatency(p)}s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {error && <div className="text-xs text-red-600 bg-red-50 p-2 rounded">{error}</div>}
          {savedAt && <div className="text-xs text-emerald-700 bg-emerald-50 p-2 rounded">✅ 저장됨</div>}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs hover:bg-gray-100 rounded">취소</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
